import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification/ToastrNotif.module';

interface UnsubscriberDTO {
  id: number;
  email: string;
  createdAt?: string;
}

function Unsubscribers() {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [items, setItems] = useState<UnsubscriberDTO[]>([]);

  const canLoad = useMemo(() => Boolean(jwt && company?.id), [jwt, company?.id]);

  const fetchItems = async() => {
    if (!canLoad) { return; }
    setLoading(true);
    try {
      const response = await fetch(`/api/companies/${company!.id}/unsubscribers`, JWTHeader(jwt));
      if (!response.ok) { throw new Error('Failed to fetch unsubscribers'); }
      const data: UnsubscriberDTO[] = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching unsubscribers :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des désinscriptions.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async() => {
    if (!canLoad) { return; }
    setExporting(true);
    try {
      const response = await fetch(`/api/companies/${company!.id}/unsubscribers/export/csv`, JWTHeader(jwt));
      if (!response.ok) { throw new Error('CSV export failed'); }
      const blobby = await response.blob();
      const objectUrl = window.URL.createObjectURL(blobby);
      const anchor = document.createElement('a');
      document.body.appendChild(anchor);
      anchor.href = objectUrl;
      anchor.download = `desinscriptions-${company!.siren}.csv`;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error exporting unsubscribers :', error);
      showToast('error', 'Une erreur est survenue lors de la génération du fichier.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (canLoad) {
      fetchItems();
    }
  }, [canLoad]);

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-8 flex items-center">
          <button
            onClick={() => navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST)}
            className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
          >
            <span>Retour</span>
          </button>
        </div>

        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Désinscriptions e-mail</h1>
            <p className="mt-2 text-gray-600">
              Consultez et exportez la liste des adresses désinscrites pour votre entreprise.
            </p>
          </div>
          <button
            onClick={handleExport}
            className="rounded-lg bg-rgpd-green px-6 py-2 text-white transition-colors hover:bg-rgpd-green-dark disabled:opacity-50"
            disabled={exporting || loading || !items.length}
          >
            {exporting ? 'Export en cours…' : 'Exporter CSV'}
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            <div className="h-4 w-48 rounded bg-gray-200"></div>
            <div className="overflow-hidden rounded-lg border">
              <div className="bg-gray-100 px-6 py-3">
                <div className="flex gap-6">
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-200"></div>
                  <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                </div>
              </div>
              <div className="divide-y">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4">
                    <div className="h-4 w-64 animate-pulse rounded bg-gray-200"></div>
                    <div className="h-4 w-40 animate-pulse rounded bg-gray-200"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-6 py-4 text-sm text-gray-500">Aucune désinscription pour le moment.</td>
                  </tr>
                )}
                {items.map((u) => (
                  <tr key={u.id}>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">{u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Unsubscribers;


